import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import qs from 'qs';
import apiClient from '@/lib/apiClient';
import { CreditNotesByMakerOrg, fetchCreditNotes, extractMarketsWithCreditNotes } from './useCreditNotes';
import {
  SEADsfFundingStatus,
  SEASupplierMarketClearsParams,
  SEASupplierMarketClearsResponse,
  SupplierMarketHistory,
} from './useTakerHistory';
import { SEASupplierMarketClearsStatsParams, SEASupplierMarketClearsStatsResponse } from './useTakerHistoryStats';
import { TakerMarket, fetchTakerMarketsWithoutUserSuppliers } from './useTakerMarkets';

/** Params for the supplier-market-clears endpoint, excluding `supplierMarkets` which is built internally from takerUuid/marketUuid/makerOrganizationUuid. */
export type ClearHistoryParams = Omit<NonNullable<SEASupplierMarketClearsParams>, 'supplierMarkets'>;

/** Params for the supplier-market-clears/stats endpoint, excluding `supplierMarkets` which is built internally. */
export type ClearHistoryStatsParams = Omit<NonNullable<SEASupplierMarketClearsStatsParams>, 'supplierMarkets'>;

export interface ClearHistory {
  // Required fields - available in both legacy and SEA APIs
  clearedAprWeightedAvg: number;
  clearedDiscountWeightedAvg: number;
  clearedDiscountedAmount: number;
  clearedDpeWeightedAvg: number;
  clearedEarn: number;
  clearedInvoiceAmount: number;
  clearedInvoiceCount: number;
  currency: string;
  isPendingClear: boolean;
  isPendingMarketClear: boolean;
  makerOrganizationUuid: string;
  marketId: string;
  marketUuid: string;
  takerUuid: string;
  payDate: string;
  depositDate: string;
  takerId: number;
  id: number;
  dsfFundingStatus?: SEADsfFundingStatus;
  hasCreditNote: boolean;
}

export async function fetchClears(
  params: ClearHistoryParams,
  takerUuid: string,
  marketUuid: string,
  makerOrganizationUuid: string
) {
  const supplierMarketClearsResponse = await fetchSupplierMarketClears(
    takerUuid,
    marketUuid,
    makerOrganizationUuid,
    params
  );

  return supplierMarketClearsResponse?.data.clears || [];
}

export default function useClearHistory(
  takerUuid: string,
  marketUuid: string,
  makerOrganizationUuid: string,
  params: ClearHistoryParams,
  options?: { enabled?: boolean }
) {
  const enabled = typeof options?.enabled === 'boolean' ? options.enabled : true;

  const results = useQueries({
    queries: [
      {
        queryKey: ['taker-markets'],
        queryFn: () => fetchTakerMarketsWithoutUserSuppliers({ includeInactive: true }), // always include inactive markets
        placeholderData: keepPreviousData,
        enabled: enabled,
      },
      {
        queryKey: ['credit-notes'],
        queryFn: () => fetchCreditNotes(),
        placeholderData: keepPreviousData,
      },
      {
        queryKey: ['clear-history', takerUuid, marketUuid, makerOrganizationUuid, params],
        queryFn: () => fetchClears(params, takerUuid, marketUuid, makerOrganizationUuid),
        placeholderData: keepPreviousData,
        enabled: enabled,
      },
    ],
  });

  const [takerMarketsResult, creditNotesResult, clearHistoryResult] = results;

  // Handle fetching states
  const isFetching = results.some((result) => result.isFetching);

  // Return loading state if any query is fetching and we don't have data yet
  if (isFetching && !clearHistoryResult.data) {
    return {
      data: undefined,
      isFetching,
    };
  }

  // All queries successful, combine the data
  const takerMarkets = (takerMarketsResult.data as TakerMarket[]) ?? [];
  const creditNotesData = creditNotesResult.data ?? [];
  const rawClearHistoryData = clearHistoryResult.data;

  if (!rawClearHistoryData) {
    return {
      data: [],
      isFetching,
    };
  }

  // Extract markets with credit notes from credit notes data
  const marketsWithCreditNotes = extractMarketsWithCreditNotes(creditNotesData as CreditNotesByMakerOrg[]);

  // Transform data based on API type

  // Transform SEA API data to ClearHistory format
  const transformedData: ClearHistory[] = (rawClearHistoryData as SupplierMarketHistory[]).map((item) => {
    // Find the corresponding taker market for the clear to enrich the data
    const takerMarket = takerMarkets.find(
      (tm: TakerMarket) =>
        tm.marketUuid === item.supplierMarket.marketUuid &&
        tm.takerDivisionUuid === item.supplierMarket.supplierDivisionUuid &&
        tm.makerOrganizationUuid === item.supplierMarket.makerOrganizationUuid
    );

    return transformSupplierMarketClearToClearHistory(item, takerMarket, marketsWithCreditNotes);
  });

  return {
    data: transformedData,
    isFetching,
  };
}

export interface ClearHistoryStats {
  clearedDiscountWeightedAvg: number;
  clearedEarn: number;
  clearedInvoiceAmount: number;
  minimumClearPayDate?: string;
  minimumClearDepositDate?: string;
  latestClearPayDate?: string;
  latestClearDepositDate?: string;
}

export async function fetchClearHistory(
  takerUuid: string,
  marketUuid: string,
  makerOrganizationUuid: string,
  params: ClearHistoryStatsParams
) {
  const searchParams = qs.stringify(
    {
      ...params,
      supplierMarkets: [{ marketUuid, supplierDivisionUuid: takerUuid, makerOrganizationUuid }],
    } satisfies SEASupplierMarketClearsStatsParams,
    { arrayFormat: 'indices' }
  );

  const supplierMarketClearsResponse = await apiClient
    .get(`api/supplier-experience/supplier-market-clears/stats`, {
      searchParams: searchParams,
    })
    .json<SEASupplierMarketClearsStatsResponse>();

  const seaData = supplierMarketClearsResponse?.data;
  if (!seaData) {
    return {
      clearedInvoiceAmount: 0,
      clearedEarn: 0,
      clearedDiscountWeightedAvg: 0,
      minimumClearPayDate: undefined,
      minimumClearDepositDate: undefined,
      latestClearPayDate: undefined,
      latestClearDepositDate: undefined,
    };
  }

  return {
    clearedInvoiceAmount: seaData.clearedInvoiceAmount || 0,
    clearedEarn: seaData.clearedEarn || 0,
    clearedDiscountWeightedAvg: seaData.clearedDiscountWeightedAvg || 0,
    minimumClearPayDate: seaData.minimumClearPayDate,
    minimumClearDepositDate: seaData.minimumClearDepositDate,
    latestClearPayDate: seaData.latestClearPayDate,
    latestClearDepositDate: seaData.latestClearDepositDate,
  };
}

export function useClearHistoryStats(
  takerUuid: string,
  marketUuid: string,
  makerOrganizationUuid: string,
  params: ClearHistoryStatsParams,
  options?: { enabled?: boolean }
) {
  const enabled = typeof options?.enabled === 'boolean' ? options.enabled : true;

  return useQuery({
    queryKey: ['clear-history-stats', takerUuid, marketUuid, params, enabled],
    queryFn: () => fetchClearHistory(takerUuid, marketUuid, makerOrganizationUuid, params),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
}

async function fetchSupplierMarketClears(
  takerUuid: string,
  marketUuid: string,
  makerOrganizationUuid: string,
  params: ClearHistoryParams
): Promise<SEASupplierMarketClearsResponse> {
  const searchParams = qs.stringify(
    {
      ...params,
      supplierMarkets: [{ marketUuid, supplierDivisionUuid: takerUuid, makerOrganizationUuid }],
    } satisfies SEASupplierMarketClearsParams,
    { arrayFormat: 'indices' }
  );

  const supplierMarketClearsResponse = await apiClient
    .get(`api/supplier-experience/supplier-market-clears`, {
      searchParams: searchParams,
    })
    .json<SEASupplierMarketClearsResponse>();

  return supplierMarketClearsResponse;
}

function transformSupplierMarketClearToClearHistory(
  item: SEASupplierMarketClearsResponse['data']['clears'][0],
  takerMarket: TakerMarket | undefined,
  marketsWithCreditNotes: string[]
): ClearHistory {
  return {
    clearedAprWeightedAvg: item.clearedAprWeightedAvg,
    clearedDiscountWeightedAvg: item.clearedDiscountWeightedAvg,
    clearedDiscountedAmount: item.clearedDiscountedAmount,
    clearedDpeWeightedAvg: item.clearedDpeWeightedAvg,
    clearedEarn: item.clearedEarn,
    clearedInvoiceAmount: item.clearedInvoiceAmount,
    clearedInvoiceCount: item.clearedInvoiceCount,
    currency: item.currency,
    makerOrganizationUuid: item.supplierMarket.makerOrganizationUuid,
    isPendingClear: item.isPendingClear,
    isPendingMarketClear: item.isPendingMarketClear,
    payDate: item.payDate,
    depositDate: item.depositDate,
    marketUuid: item.supplierMarket.marketUuid,
    takerUuid: item.supplierMarket.supplierDivisionUuid,
    dsfFundingStatus: item.dsfFundingStatus,
    id: item.clearIds?.[0], // We know API returns clear ids. fill it for now. we dont use this anywhere
    hasCreditNote: marketsWithCreditNotes.includes(item.supplierMarket.marketUuid),

    // enrich below data from useTakerMarkets hook
    takerId: takerMarket?.takerDivisionId ?? 0,
    marketId: item.supplierMarket.marketUuid, // Using UUID as marketId in existing code
  };
}
